import React, { useState, useRef, DragEvent, TouchEvent, useEffect } from 'react';
import { compressImage, PRODUCT_IMAGE_OPTIONS } from '../utils/imageCompression';
import { ACCEPTED_IMAGE_INPUT_TYPES, ImageValidationError, validateImageFile } from '../utils/imageValidation';
import './ImageUpload.css';

export interface ImageFile {
  id: string;
  file: File | null;
  preview: string;
  isExisting?: boolean;
  imageId?: string;
}

interface ImageUploadProps {
  images: ImageFile[];
  onImagesChange: (images: ImageFile[]) => void;
  maxImages?: number;
  hint?: string;
  onImageClick?: (index: number) => void;
  validationMessages?: Record<ImageValidationError | 'tooMany' | 'processingFailed', string>;
  /** True while picked photos are still being compressed and are not yet part of `images`. */
  onProcessingChange?: (processing: boolean) => void;
}

export const PRODUCT_IMAGE_LIMIT = 10;

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = PRODUCT_IMAGE_LIMIT,
  hint = 'Prima imagine va fi imaginea principală a anunțului tău.',
  onImageClick,
  validationMessages,
  onProcessingChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [processingCount, setProcessingCount] = useState<number>(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [touchStartIndex, setTouchStartIndex] = useState<number | null>(null);
  const [isDraggingEnabled, setIsDraggingEnabled] = useState(false);
  const isDraggingEnabledRef = useRef(false);
  const touchStartIndexRef = useRef<number | null>(null);
  const dragOverIndexRef = useRef<number | null>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoScrollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    onProcessingChange?.(processingCount > 0);
  }, [processingCount, onProcessingChange]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = maxImages - images.length;
    const selectedFiles = Array.from(files).slice(0, remainingSlots);
    const validFiles = selectedFiles.filter(file => {
      const error = validateImageFile(file);
      if (error && validationMessages) {
        setValidationError(validationMessages[error]);
      }
      return error === null;
    });

    if (files.length > remainingSlots && validationMessages) {
      setValidationError(validationMessages.tooMany);
    } else if (validFiles.length === selectedFiles.length) {
      setValidationError(null);
    }

    // Set processing count to show loading indicators
    setProcessingCount(validFiles.length);

    const processImage = async (file: File, index: number): Promise<ImageFile | null> => {
      try {
        // Compress the image before processing
        const compressedFile = await compressImage(file, PRODUCT_IMAGE_OPTIONS);

        return new Promise<ImageFile>((resolve) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            const preview = event.target?.result as string;
            resolve({
              id: `${Date.now()}-${index}-${Math.random()}`,
              file: compressedFile,
              preview,
              isExisting: false,
            });
          };
          reader.readAsDataURL(compressedFile);
        });
      } catch (error) {
        console.error('Error processing image:', error);
        setValidationError(validationMessages?.processingFailed ?? 'Imaginea nu a putut fi procesată.');
        return null;
      }
    };

    // Process all images in parallel
    const promises: Promise<ImageFile | null>[] = [];
    for (let i = 0; i < validFiles.length; i++) {
      promises.push(processImage(validFiles[i], i));
    }

    const processedImages = await Promise.all(promises);
    const validImages = processedImages.filter((img): img is ImageFile => img !== null);

    // Update images and reset processing count
    onImagesChange([...images, ...validImages]);
    setProcessingCount(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const handleDragStart = (index: number) => {
    // Clear any touch states when desktop drag starts
    setTouchStartIndex(null);
    setIsDraggingEnabled(false);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setDraggedIndex(index);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    onImagesChange(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>, index: number) => {
    // Don't interfere with regular touch/scroll behavior
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    touchStartIndexRef.current = index;
    setTouchStartIndex(index);

    // Start a timer for long press (1 second)
    longPressTimer.current = setTimeout(() => {
      // Only enable dragging if the touch is still active and hasn't moved much
      if (touchStartPos.current) {
        console.log('✅ Drag enabled - index:', index);
        isDraggingEnabledRef.current = true;
        touchStartIndexRef.current = index; // Make sure this is set again
        dragOverIndexRef.current = index;
        setIsDraggingEnabled(true);
        setDraggedIndex(index);
        setDragOverIndex(index); // Initialize to current position
        // Optionally add haptic feedback on supported devices
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    }, 1000);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];

    // If drag is not enabled yet, just check if user moved and cancel the long press timer
    if (!isDraggingEnabledRef.current) {
      if (touchStartPos.current && longPressTimer.current) {
        const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

        // If user moves finger before long press completes, cancel and allow scroll
        if (deltaX > 10 || deltaY > 10) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
          touchStartIndexRef.current = null;
          setTouchStartIndex(null);
          touchStartPos.current = null;
        }
      }
      // Critical: Don't prevent default - allow native scrolling
      return;
    }

    // From here on, drag is active, so we can safely prevent default
    if (!touchStartPos.current || touchStartIndexRef.current === null) return;

    // Only prevent default and handle dragging if long press was activated
    // Use ref for reliable check
    if (isDraggingEnabledRef.current && touchStartIndexRef.current !== null) {
      e.preventDefault();

      const touchY = touch.clientY;
      const viewportHeight = window.innerHeight;
      const scrollThreshold = 200; // Larger zone for triggering scroll (covers header area)

      // Clear any existing auto-scroll
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
        autoScrollInterval.current = null;
      }

      // Check if near top edge (header area)
      if (touchY < scrollThreshold) {
        const distanceFromEdge = touchY;
        // Progressive speed: faster when closer to edge
        const intensity = 1 - (distanceFromEdge / scrollThreshold);
        const scrollSpeed = Math.max(2, intensity * 15); // 2-15px per frame

        // Use smooth scrolling with requestAnimationFrame
        let lastTime = performance.now();
        const scroll = () => {
          const currentTime = performance.now();
          const deltaTime = currentTime - lastTime;
          lastTime = currentTime;

          // Smooth scroll based on time delta (for consistent speed across devices)
          window.scrollBy({
            top: -scrollSpeed,
            behavior: 'auto'
          });

          autoScrollInterval.current = setTimeout(() => {
            scroll();
          }, 16) as any; // ~60fps
        };
        scroll();
      }
      // Check if near bottom edge
      else if (touchY > viewportHeight - scrollThreshold) {
        const distanceFromEdge = viewportHeight - touchY;
        const intensity = 1 - (distanceFromEdge / scrollThreshold);
        const scrollSpeed = Math.max(2, intensity * 15);

        let lastTime = performance.now();
        const scroll = () => {
          const currentTime = performance.now();
          const deltaTime = currentTime - lastTime;
          lastTime = currentTime;

          window.scrollBy({
            top: scrollSpeed,
            behavior: 'auto'
          });

          autoScrollInterval.current = setTimeout(() => {
            scroll();
          }, 16) as any;
        };
        scroll();
      }

      // Find which element we're over (excluding loading items)
      const allItems = Array.from(document.querySelectorAll('.image-upload-item:not(.image-upload-loading)'));

      // Try element from point first
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const closestItem = element?.closest('.image-upload-item:not(.image-upload-loading)');

      let targetIndex = -1;

      if (closestItem) {
        targetIndex = allItems.indexOf(closestItem as Element);
      }

      // Fallback to distance calculation if element from point fails
      if (targetIndex === -1) {
        let minDistance = Infinity;

        allItems.forEach((item, idx) => {
          const rect = item.getBoundingClientRect();

          // Check if touch point is within the item's bounds
          if (
            touch.clientX >= rect.left &&
            touch.clientX <= rect.right &&
            touch.clientY >= rect.top &&
            touch.clientY <= rect.bottom
          ) {
            targetIndex = idx;
            return;
          }

          // Calculate distance to center
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          const distance = Math.sqrt(
            Math.pow(touch.clientX - centerX, 2) +
            Math.pow(touch.clientY - centerY, 2)
          );

          if (distance < minDistance) {
            minDistance = distance;
            targetIndex = idx;
          }
        });
      }

      if (targetIndex !== -1) {
        dragOverIndexRef.current = targetIndex;
        setDragOverIndex(targetIndex);
      }
    }
  };

  const handleTouchCancel = (e: TouchEvent<HTMLDivElement>) => {
    // Clear timers only - touch end will handle cleanup
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    // Get current values from refs before clearing anything
    const dragEnabled = isDraggingEnabledRef.current;
    const startIdx = touchStartIndexRef.current;
    const dropIdx = dragOverIndexRef.current;

    // If already cleared, this is a duplicate call - ignore it
    if (startIdx === null && dragEnabled === false) {
      return;
    }

    // Clear long press timer if still running
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Clear auto-scroll interval
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }

    // Only perform reorder if dragging was enabled and we have a valid drop target
    // Use refs for reliable state check
    if (dragEnabled && startIdx !== null && dropIdx !== null) {
      if (startIdx !== dropIdx) {
        console.log('✅ Reordered:', startIdx, '→', dropIdx);
        const newImages = [...images];
        const draggedImage = newImages[startIdx];
        newImages.splice(startIdx, 1);
        newImages.splice(dropIdx, 0, draggedImage);
        onImagesChange(newImages);
      }
    }

    // Reset all touch states
    touchStartIndexRef.current = null;
    dragOverIndexRef.current = null;
    isDraggingEnabledRef.current = false;
    setTouchStartIndex(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingEnabled(false);
    touchStartPos.current = null;
  };

  return (
    <div className="image-upload-component">
      <div className="image-upload-header">
        <div className="image-upload-title-row">
          <h2 className="image-upload-title">Imagini</h2>
          <span className="image-upload-count" aria-label={`${images.length + processingCount} din ${maxImages} imagini`}>
            {images.length + processingCount}/{maxImages}
          </span>
        </div>
        <p className="image-upload-hint">{hint}</p>
      </div>
      {validationError && <p className="image-upload-error" role="alert">{validationError}</p>}
      <div className="image-upload-wrapper">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_INPUT_TYPES}
          multiple
          onChange={handleFileSelect}
          className="image-upload-input-hidden"
        />
        <div className="image-upload-grid">
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`image-upload-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''} ${touchStartIndex === index && !isDraggingEnabled ? 'long-pressing' : ''}`}
              draggable={true}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(e, index)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
              onContextMenu={(e) => e.preventDefault()}
            >
              <img
                src={image.preview}
                alt={`Upload ${index + 1}`}
                className="image-upload-preview"
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
                onClick={() => onImageClick?.(index)}
                style={{ cursor: onImageClick ? 'pointer' : 'default' }}
              />
              <button
                className="image-upload-remove-button"
                onClick={() => handleRemoveImage(index)}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.6667 4.27301L11.7267 3.33301L8.00004 7.05967L4.27337 3.33301L3.33337 4.27301L7.06004 7.99967L3.33337 11.7263L4.27337 12.6663L8.00004 8.93967L11.7267 12.6663L12.6667 11.7263L8.94004 7.99967L12.6667 4.27301Z" fill="#FFF"/>
                </svg>
              </button>
              {index === 0 && (
                <div className="image-upload-main-badge">Imagine principală</div>
              )}
            </div>
          ))}
          {Array.from({ length: processingCount }).map((_, index) => (
            <div
              key={`loading-${index}`}
              className="image-upload-item image-upload-loading"
            >
              <div className="image-upload-loading-content">
                <div className="image-upload-spinner"></div>
                <p className="image-upload-loading-text">Se comprimă...</p>
              </div>
            </div>
          ))}
          {images.length + processingCount < maxImages && (
            <button
              type="button"
              className="image-upload-add-button"
              onClick={handleUploadClick}
              aria-label="Adaugă imagini"
            >
              <span className="image-upload-add-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M13 11V5C13 4.447 12.552 4 12 4C11.448 4 11 4.447 11 5V11H5C4.448 11 4 11.447 4 12C4 12.553 4.448 13 5 13H11V19C11 19.553 11.448 20 12 20C12.552 20 13 19.553 13 19V13H19C19.552 13 20 12.553 20 12C20 11.447 19.552 11 19 11H13Z" fill="#267200"/>
                </svg>
                <span>Adaugă imagini</span>
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
